import moment from 'moment/moment';
import React, { useEffect, useRef, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { plannerInfluencer } from '../../../store/influencer/plannerSlice';
import { IAllStates } from '../../../store/rootReducer';
import { ErrorToast } from '../../../utils/toasters';
import PlannerCalendarCard from './components/PlannerCalendarCard';
import PlannerCard from './components/PlannerCard';
import { IPlannerData, IPlannerEventsQuery, IPlannerOffers } from './dto/IPlanner';
import { InfluencerPlannerService } from './service';
import { StoreKeeper } from '../../../store/store';
import { loadingSlice } from '../../../store/general/loadingSlice';

export interface ISelectedYearMonth {
    currentYear: string;
    currentMonth: string;
}

interface ILocation {
    campaignId: number;
    publishFrom?: string;
}

const InfluencerPlanner = () => {
    const dispatch = useDispatch();
    StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));
    const { state } = useLocation();
    const navigate = useNavigate();
    const [offers, setOffers] = useState<IPlannerOffers>();
    const [isOffersLoading, setIsOffersLoading] = useState(true);
    const lastOffersKeyRef = useRef<string>();
    const { filterParams, updateApi, triggerRescheduleList } = useSelector(
        (state: IAllStates) => state.plannerInfluencer
    );
    const [selectedYearMonth, setSelectedYearMonth] = useState<ISelectedYearMonth>({
        currentYear: moment(new Date()).format('Y'),
        currentMonth: moment(new Date()).format('M'),
    });
    const [selectedCalendarView, setSelectedCalendarView] = useState<'dayGridMonth' | 'dayGridWeek' | 'listWeek'>(
        'listWeek'
    );

    const campaignId = (state as ILocation)?.campaignId ?? (filterParams as unknown as IPlannerEventsQuery)?.campaignId;

    useEffect(() => {
        const queryKey = `${campaignId}-${filterParams?.page}`;
        if (lastOffersKeyRef.current !== queryKey) {
            lastOffersKeyRef.current = queryKey;
            setIsOffersLoading(true);
        }

        let isCurrent = true;
        InfluencerPlannerService.getPlannerOffers({
            ...filterParams,
            perPage: 10,
            campaignId,
        })
            .then(response => {
                if (!isCurrent) return;
                setOffers({
                    ...response,
                    data: response?.data,
                    info: response?.info,
                    totalPlanned: response?.totalPlanned,
                });
                dispatch(
                    plannerInfluencer.actions.setFilterParams({
                        totalPages: Math.ceil(response?.count / 10),
                    })
                );
                dispatch(
                    plannerInfluencer.actions.setCampaigns(
                        response.data?.map((campaign: IPlannerData) => ({
                            ...campaign.campaign,
                            offers: [campaign],
                            offerId: campaign?.id,
                            key: campaign?.id,
                        }))
                    )
                );
                if ((state as ILocation)?.campaignId) {
                    dispatch(
                        plannerInfluencer.actions.setFilterParams({ campaignId: (state as ILocation)?.campaignId })
                    );
                    navigate('.', { replace: true, state: undefined });
                    dispatch(
                        plannerInfluencer.actions.setSelectedCampaign(
                            response?.data?.find(
                                (campaign: IPlannerData) => campaign.campaign.id === (state as ILocation)?.campaignId
                            )?.campaign
                        )
                    );
                }
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsOffersLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [(filterParams as unknown as IPlannerEventsQuery)?.campaignId, filterParams?.page, updateApi]);

    useEffect(() => {
        if ((state as ILocation)?.publishFrom) {
            setSelectedYearMonth({
                currentYear: moment((state as ILocation)?.publishFrom).format('Y'),
                currentMonth: moment((state as ILocation)?.publishFrom).format('M'),
            });
        }
    }, [(state as ILocation)?.publishFrom]);

    useEffect(() => {
        InfluencerPlannerService.getEvents({
            ...filterParams,
            currentYear: selectedYearMonth?.currentYear,
            currentMonth: +selectedYearMonth?.currentMonth,
        } as any)
            .then(response => {
                dispatch(plannerInfluencer.actions.setEvents(response.data));
            })
            .catch(error => ErrorToast(error));
    }, [filterParams, JSON.stringify(selectedYearMonth), selectedCalendarView, triggerRescheduleList]);

    return (
        <Row className="mb-5 pb-5 flex-column-reverse flex-md-row">
            <Col lg={8} xl={9}>
                <PlannerCalendarCard
                    selectedYearMonth={selectedYearMonth}
                    selectedCalendarView={selectedCalendarView}
                    setSelectedCalendarView={setSelectedCalendarView}
                    setSelectedYearMonth={setSelectedYearMonth}
                    publishFrom={(state as ILocation)?.publishFrom}
                />
            </Col>
            <Col lg={4} xl={3}>
                <PlannerCard offers={offers as IPlannerOffers} isLoading={isOffersLoading} />
            </Col>
        </Row>
    );
};

export default InfluencerPlanner;
